import { useEffect, useRef, useCallback, useMemo } from 'react';
import { isSafari, isElectron } from 'react-device-detect';

interface UseDeepLinkProps {
    links: string | string[];
    onFallback?: (e?: Error) => void;
    onLinked?: () => void;
    fallbackDelay?: number;
}

export const useDeepLinking = (
    { links, onLinked, onFallback, fallbackDelay = 4000 }: UseDeepLinkProps,
    isActive = true,
) => {
    const attemptedRef = useRef(false);
    const openedRef = useRef(false);
    const fallbackTimerRef = useRef<number | null>(null);
    const linkIndexRef = useRef(0);
    const isFalled = useRef(false);

    const deepLinks = useMemo(() => (Array.isArray(links) ? links : [links]), [links]);

    const clearTimer = useCallback(() => {
        if (fallbackTimerRef.current !== null) {
            clearTimeout(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
        }
    }, []);

    const markOpened = useCallback(() => {
        if (openedRef.current) return;
        openedRef.current = true;
        clearTimer();
        if (!isFalled.current) {
            onLinked?.();
        }
    }, [clearTimer, onLinked]);

    const handleFallback = useCallback(
        (e?: Error) => {
            isFalled.current = true;
            onFallback?.(e);
        },
        [onFallback],
    );

    const tryOpenLink = useCallback(() => {
        if (deepLinks.length === 0) {
            console.warn('No deep link found.');
            handleFallback();

            return;
        }
        const currentLink = deepLinks[linkIndexRef.current];

        try {
            if (isSafari) {
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = currentLink;
                document.body.appendChild(iframe);
            } else {
                setTimeout(() => {
                    window.location.href = currentLink;
                }, 200);
            }

            fallbackTimerRef.current = window.setTimeout(() => {
                if (openedRef.current) return;

                linkIndexRef.current += 1;

                if (linkIndexRef.current < deepLinks.length) {
                    tryOpenLink();
                } else {
                    handleFallback();
                }
            }, fallbackDelay);
        } catch (e) {
            handleFallback(e instanceof Error ? e : new Error(String(e)));
        }
    }, [deepLinks, fallbackDelay, handleFallback]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (attemptedRef.current) {
            return;
        }
        if (!isActive) {
            attemptedRef.current = false;

            return;
        }
        attemptedRef.current = true;

        if (isElectron) {
            onLinked?.();

            return;
        }

        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') markOpened();
        };

        const handlePageHide = () => markOpened();
        const handleBlur = () => markOpened();

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('blur', handleBlur);

        tryOpenLink();

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('blur', handleBlur);
            clearTimer();
        };
    }, [markOpened, clearTimer, tryOpenLink, onLinked, isActive]);
};
